import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Plus, Pencil, Trash2, Package, Mail, MousePointerClick, X, Star, ArrowUpDown, TrendingUp } from "lucide-react";
import { api, formatPrice, BADGES, formatApiErrorDetail, usePageMeta } from "@/lib/api";

const EMPTY_FORM = {
  name: "", category: "", problem: "", solution: "", description: "",
  price: "", badge: "", image: "", images: "", amazon_url: "", tags: "", provisional: false, featured: false,
};

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("s360_token", data.token);
      onLogin(data.user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-400";

  return (
    <div className="min-h-screen bg-slate-950 hero-grain flex items-center justify-center px-4">
      <form onSubmit={submit} className="bg-white rounded-2xl p-8 w-full max-w-sm shadow-2xl" data-testid="admin-login-form">
        <p className="font-display font-extrabold text-slate-900 text-xl tracking-tight">
          sols<span className="text-amber-500">diarias</span>360
        </p>
        <p className="text-sm text-slate-500 mt-1 mb-6">Panel de administración</p>
        <label className="text-sm font-semibold text-slate-800 block mb-1.5">Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
          data-testid="admin-email-input" className={`${field} mb-4`} placeholder="admin@solsdiarias360.com" />
        <label className="text-sm font-semibold text-slate-800 block mb-1.5">Contraseña</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
          data-testid="admin-password-input" className={field} placeholder="••••••••" />
        {error && <p className="text-sm text-red-600 mt-3" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={loading} data-testid="admin-login-button"
          className="w-full mt-6 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-slate-950 font-bold py-3 rounded-lg transition-colors">
          {loading ? "Entrando..." : "Entrar"}
        </button>
        <Link to="/" className="block text-center text-xs text-slate-500 hover:text-amber-600 mt-4">Volver a la tienda</Link>
      </form>
    </div>
  );
};

const ProductForm = ({ initial, categories, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const field = "w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-400";

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSave(form); }}
      className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4"
      data-testid="product-form"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-slate-900">{form.id ? "Editar producto" : "Nuevo producto"}</h3>
        <button type="button" onClick={onCancel} data-testid="product-form-cancel" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-slate-700 block mb-1">Nombre *</label>
          <input required minLength={3} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pf-name" className={field} />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">Categoría *</label>
          <select required value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="pf-category" className={field}>
            <option value="">Elige una categoría</option>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">Etiqueta</label>
          <select value={form.badge} onChange={(e) => set("badge", e.target.value)} data-testid="pf-badge" className={field}>
            <option value="">Ninguna</option>
            <option value="favorito">Favorito</option>
            <option value="oferta">Merece la pena</option>
            <option value="nuevo">Novedad</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">Precio (€)</label>
          <input type="number" step="0.01" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="pf-price" className={field} />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">Problemas que resuelve (tags, separados por comas)</label>
          <input value={form.tags} onChange={(e) => set("tags", e.target.value)} data-testid="pf-tags" className={field} placeholder="espacio, limpieza, cocina, cables, viaje, coche" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-slate-700 block mb-1">Problema que resuelve</label>
          <input value={form.problem} onChange={(e) => set("problem", e.target.value)} data-testid="pf-problem" className={field} />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-slate-700 block mb-1">Solución que aporta</label>
          <input value={form.solution} onChange={(e) => set("solution", e.target.value)} data-testid="pf-solution" className={field} />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-slate-700 block mb-1">Descripción</label>
          <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="pf-description" className={field} />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-slate-700 block mb-1">Imagen principal (URL)</label>
          <input value={form.image} onChange={(e) => set("image", e.target.value)} data-testid="pf-image" className={field} placeholder="https://..." />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-slate-700 block mb-1">Más fotos del producto (una URL por línea)</label>
          <textarea rows={3} value={form.images} onChange={(e) => set("images", e.target.value)} data-testid="pf-images" className={field}
            placeholder={"https://ejemplo.com/foto2.jpg\nhttps://ejemplo.com/foto3.jpg"} />
          {(form.image.trim() || form.images.trim()) && (
            <div className="flex gap-2 mt-2 flex-wrap" data-testid="pf-image-previews">
              {[form.image, ...form.images.split("\n")].map((s) => s.trim()).filter(Boolean).map((src, i) => (
                <img key={i} src={src} alt={`Vista previa ${i + 1}`} className="w-14 h-14 object-cover rounded-lg border border-slate-200 bg-white" />
              ))}
            </div>
          )}
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-slate-700 block mb-1">Enlace de afiliado de Amazon</label>
          <input value={form.amazon_url} onChange={(e) => set("amazon_url", e.target.value)} data-testid="pf-amazon-url" className={field} placeholder="https://www.amazon.es/dp/...?tag=tu-id-21" />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="pf-featured" className="accent-amber-500" />
        Destacado en portada (aparece en la sección Favoritos de la página principal)
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={form.provisional} onChange={(e) => set("provisional", e.target.checked)} data-testid="pf-provisional" className="accent-amber-500" />
        Marcar como ficha provisional (se muestra un aviso en la web)
      </label>
      <button type="submit" disabled={saving} data-testid="product-form-save"
        className="bg-slate-950 hover:bg-slate-800 disabled:opacity-60 text-white font-bold px-6 py-2.5 rounded-lg text-sm transition-colors">
        {saving ? "Guardando..." : "Guardar producto"}
      </button>
    </form>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [sortByClicks, setSortByClicks] = useState(false);

  usePageMeta("Administración · solsdiarias360", "Panel de administración.");

  useEffect(() => {
    const token = localStorage.getItem("s360_token");
    if (!token) { setChecking(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("s360_token"))
      .finally(() => setChecking(false));
  }, []);

  const loadData = () => {
    api.get("/products", { params: { limit: 500 } }).then((r) => setProducts(r.data.products)).catch(() => {});
    api.get("/messages").then((r) => setMessages(r.data)).catch(() => setMessages([]));
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  };

  useEffect(() => { if (user) loadData(); }, [user]);

  const toForm = (p) => p
    ? { ...p, price: p.price ?? "", badge: p.badge || "", tags: (p.tags || []).join(", "), images: (p.images || []).join("\n"), featured: !!p.featured }
    : { ...EMPTY_FORM };

  const saveProduct = async (form) => {
    setSaving(true);
    setNotice("");
    const payload = {
      name: form.name,
      category: form.category,
      problem: form.problem,
      solution: form.solution,
      description: form.description,
      price: form.price === "" ? null : parseFloat(form.price),
      badge: form.badge || null,
      image: form.image,
      images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
      amazon_url: form.amazon_url,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      provisional: form.provisional,
      featured: form.featured,
    };
    try {
      if (form.id) await api.put(`/products/${form.id}`, payload);
      else await api.post("/products", payload);
      setEditing(null);
      setNotice("Producto guardado correctamente.");
      loadData();
    } catch (err) {
      setNotice(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeProduct = async (id) => {
    if (!window.confirm("¿Eliminar este producto? Esta acción no se puede deshacer.")) return;
    await api.delete(`/products/${id}`).catch(() => {});
    loadData();
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("s360_token");
    setUser(null);
  };

  if (checking) return <div className="min-h-screen bg-slate-950" />;
  if (!user) return <LoginForm onLogin={setUser} />;

  const totalClicks = products.reduce((s, p) => s + (p.clicks || 0), 0);
  const featuredCount = products.filter((p) => p.featured).length;
  const sortedProducts = sortByClicks ? [...products].sort((a, b) => (b.clicks || 0) - (a.clicks || 0)) : products;
  const topClicked = [...products].sort((a, b) => (b.clicks || 0) - (a.clicks || 0)).filter((p) => (p.clicks || 0) > 0).slice(0, 5);

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <p className="font-display font-extrabold tracking-tight text-lg">
            sols<span className="text-amber-400">diarias</span>360 <span className="text-slate-500 font-normal text-sm">· administración</span>
          </p>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs text-slate-400 hover:text-amber-400" data-testid="admin-back-store">Ver la tienda</Link>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-1.5 text-xs font-semibold bg-slate-800 hover:bg-slate-700 px-3 py-2 rounded-lg transition-colors">
              <LogOut size={13} /> Salir
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          {[
            { icon: Package, label: "Productos", value: products.length },
            { icon: MousePointerClick, label: "Clics a Amazon", value: totalClicks },
            { icon: Mail, label: "Mensajes", value: messages.length },
            { icon: Star, label: "En portada", value: featuredCount },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4">
              <s.icon size={18} className="text-amber-500 mb-2" />
              <p className="font-display text-2xl font-bold text-slate-900">{s.value}</p>
              <p className="text-xs text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-6">
          {[["products", "Productos"], ["messages", "Mensajes"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === id ? "bg-slate-950 text-white" : "bg-white text-slate-600 border border-slate-300"}`}>
              {label}
            </button>
          ))}
        </div>

        {notice && <p className="text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 mb-4" data-testid="admin-notice">{notice}</p>}

        {tab === "products" && (
          <div data-testid="admin-products-panel">
            {!editing && (
              <button onClick={() => setEditing(toForm(null))} data-testid="admin-new-product"
                className="mb-5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 transition-colors">
                <Plus size={16} strokeWidth={2.5} /> Nuevo producto
              </button>
            )}
            {editing && (
              <div className="mb-6">
                <ProductForm initial={editing} categories={categories} onSave={saveProduct} onCancel={() => setEditing(null)} saving={saving} />
              </div>
            )}
            {topClicked.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5" data-testid="admin-top-clicks">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
                  <TrendingUp size={14} className="text-amber-500" /> Los más clicados
                </p>
                <ol className="space-y-1.5">
                  {topClicked.map((p, i) => (
                    <li key={p.id} className="flex items-center justify-between text-sm" data-testid={`top-clicked-${i}`}>
                      <span className="text-slate-700 line-clamp-1"><span className="font-bold text-slate-400 mr-2">{i + 1}.</span>{p.name}</span>
                      <span className="font-bold text-slate-900 ml-3 shrink-0">{p.clicks} clics</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="px-4 py-3">Producto</th>
                    <th className="px-4 py-3">Categoría</th>
                    <th className="px-4 py-3">Precio</th>
                    <th className="px-4 py-3">Etiqueta</th>
                    <th className="px-4 py-3">
                      <button type="button" onClick={() => setSortByClicks((v) => !v)} data-testid="admin-sort-clicks"
                        className={`flex items-center gap-1 uppercase tracking-wider transition-colors ${sortByClicks ? "text-amber-600" : "hover:text-slate-800"}`}>
                        Clics <ArrowUpDown size={12} />
                      </button>
                    </th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedProducts.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100 last:border-0" data-testid={`admin-product-row-${p.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-900 max-w-[280px]">
                        <span className="line-clamp-1 flex items-center gap-1.5">
                          {p.featured && <Star size={13} className="text-amber-500 fill-amber-500 shrink-0" aria-label="Destacado en portada" />}
                          {p.name}
                        </span>
                        {p.provisional && <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 ml-1">provisional</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{p.category}</td>
                      <td className="px-4 py-3 text-slate-900 font-semibold">{formatPrice(p.price) || "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{p.badge ? BADGES[p.badge]?.label : "—"}</td>
                      <td className="px-4 py-3 text-slate-900">{p.clicks || 0}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => setEditing(toForm(p))} data-testid={`admin-edit-${p.id}`} className="text-slate-500 hover:text-amber-600 transition-colors" aria-label="Editar"><Pencil size={16} /></button>
                          <button onClick={() => removeProduct(p.id)} data-testid={`admin-delete-${p.id}`} className="text-slate-500 hover:text-red-600 transition-colors" aria-label="Eliminar"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "messages" && (
          <div className="space-y-3" data-testid="admin-messages-panel">
            {messages.length === 0 && <p className="text-sm text-slate-500">Todavía no hay mensajes.</p>}
            {messages.map((m) => (
              <div key={m.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`admin-message-${m.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <p className="font-semibold text-slate-900 text-sm">{m.name} <span className="text-slate-400 font-normal">· {m.email}</span></p>
                  <p className="text-xs text-slate-400">{new Date(m.created_at).toLocaleString("es-ES")}</p>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{m.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
